import { useCallback } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { Draft, produce } from 'immer'

import {
  ActiveOrgResponse,
  getActiveOrgQueryKey,
  useActiveOrgId,
} from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import {
  AuthOrg,
  BackendPasskey,
  BridgeVirtualAccount,
  OrgInvitation,
  OrgMember,
  OrgSigner,
  OrgSmartAccountsSetup,
} from '~/types'

const updateActiveOrgInQueryCache = (
  activeOrgId: string | undefined,
  queryClient: QueryClient,
  recipe: (org: Draft<AuthOrg>) => void,
) => {
  queryClient.setQueryData<ActiveOrgResponse>(
    getActiveOrgQueryKey(activeOrgId),
    (data) => {
      if (data?.org) {
        return {
          org: produce(data.org, (draft) => recipe(draft)),
          featureFlags: data.featureFlags,
        }
      }
    },
  )
}

export const useActiveOrgUpdates = () => {
  const queryClient = useQueryClient()
  const { user: authUser } = useAuthUser()
  const activeOrgId = useActiveOrgId()

  const updateActiveOrg = useCallback(
    (recipe: (org: Draft<AuthOrg>) => void) => {
      updateActiveOrgInQueryCache(activeOrgId, queryClient, recipe)
    },
    [queryClient, activeOrgId],
  )

  return {
    updateOrgMember: useCallback(
      (member: OrgMember) => {
        updateActiveOrg((org) => {
          org.members = org.members.map((m) =>
            m.id === member.id ? member : m,
          )
        })
      },
      [updateActiveOrg],
    ),
    addInvitation: useCallback(
      (invitation: OrgInvitation) => {
        updateActiveOrg((org) => {
          org.invitations.push(invitation)
        })
      },
      [updateActiveOrg],
    ),
    removeInvitation: useCallback(
      (invitationId: string) => {
        updateActiveOrg((org) => {
          org.invitations = org.invitations.filter(
            (invitation) => invitation.id !== invitationId,
          )
        })
      },
      [updateActiveOrg],
    ),
    removeOrgMember: useCallback(
      (memberId: string) => {
        updateActiveOrg((org) => {
          org.members = org.members.filter((member) => member.id !== memberId)
        })
      },
      [updateActiveOrg],
    ),
    addPasskey: useCallback(
      (passkeyData: BackendPasskey) => {
        updateActiveOrg((org) => {
          org.passkeys.push({
            ...passkeyData,
            isArchived: false,
            displayName: passkeyData.name,
            user: {
              id: authUser!.id,
              email: authUser!.email,
            },
          })
        })
      },
      [updateActiveOrg, authUser],
    ),
    updatePasskey: useCallback(
      <T extends keyof OrgSigner>(
        passkeyId: string,
        property: T,
        value: OrgSigner[T],
      ) => {
        updateActiveOrg((org) => {
          const passkey = org.passkeys.find((p) => p.id === passkeyId)
          if (passkey) {
            passkey[property] = value
          }
        })
      },
      [updateActiveOrg],
    ),
    removePasskey: useCallback(
      (passkeyId: string) => {
        updateActiveOrg((org) => {
          org.passkeys = org.passkeys.filter(
            (passkey) => passkey.id !== passkeyId,
          )
        })
      },
      [updateActiveOrg],
    ),
    updateSmartAccountsSetup: useCallback(
      (setup: OrgSmartAccountsSetup) => {
        updateActiveOrg((org) => {
          org.smartAccountSetup = setup
        })
      },
      [updateActiveOrg],
    ),
    updateName: useCallback(
      (name: string) => {
        updateActiveOrg((org) => {
          org.name = name
        })
      },
      [updateActiveOrg],
    ),
    updateLogoUrl: useCallback(
      (logoUrl: string) => {
        updateActiveOrg((org) => {
          org.logoUrl = logoUrl
        })
      },
      [updateActiveOrg],
    ),
    updateSmartAccountsChainIds: useCallback(
      (chainIds: number[]) => {
        updateActiveOrg((org) => {
          org.smartAccountsChainIds = chainIds
        })
      },
      [updateActiveOrg],
    ),
    updateBridgeData: useCallback(
      (bridgeData: AuthOrg['bridge']) => {
        updateActiveOrg((org) => {
          org.bridge = bridgeData
        })
      },
      [updateActiveOrg],
    ),
    replaceBridgeVirtualAccount: useCallback(
      (
        oldVirtualAccountId: string,
        newVirtualAccount: BridgeVirtualAccount,
      ) => {
        updateActiveOrg((org) => {
          if (!org.bridge) return
          org.bridge.virtualAccounts = org.bridge.virtualAccounts.map((v) =>
            v.id === oldVirtualAccountId ? newVirtualAccount : v,
          )
        })
      },
      [updateActiveOrg],
    ),
    updateExternalAccountName: useCallback(
      (externalAccountId: string, name: string) => {
        updateActiveOrg((org) => {
          if (org.bridge) {
            org.bridge.externalAccounts = org.bridge.externalAccounts.map(
              (externalAccount) => {
                if (externalAccount.externalAccountId === externalAccountId)
                  return {
                    ...externalAccount,
                    name,
                  }
                else return externalAccount
              },
            )
          }
        })
      },
      [updateActiveOrg],
    ),
    updateExternalAccountSource: useCallback(
      (externalAccountId: string, source: 'internal' | 'external') => {
        updateActiveOrg((org) => {
          if (org.bridge) {
            org.bridge.externalAccounts = org.bridge.externalAccounts.map(
              (externalAccount) => {
                if (externalAccount.externalAccountId === externalAccountId)
                  return {
                    ...externalAccount,
                    source,
                  }
                else return externalAccount
              },
            )
          }
        })
      },
      [updateActiveOrg],
    ),
    removeExternalAccount: useCallback(
      (externalAccountId: string) => {
        updateActiveOrg((org) => {
          if (org.bridge) {
            org.bridge.externalAccounts = org.bridge.externalAccounts.filter(
              (externalAccount) =>
                externalAccount.externalAccountId !== externalAccountId,
            )
          }
        })
      },
      [updateActiveOrg],
    ),
    updateRequireMemo: useCallback(
      (requireMemo: boolean) => {
        updateActiveOrg((org) => {
          org.requireMemo = requireMemo
        })
      },
      [updateActiveOrg],
    ),
  }
}
